import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Typography } from 'antd';
import {
  Currency,
  DateRangeFilter,
  ErrorHandler,
  NumberRangeFilter,
} from '@common/components';
import { ShiftGapType } from '@scheduling/graphql/queries/worker-detail';
import { useLazyQuery } from '@common/hooks';
import { PaginatedWorksShiftsQuery } from '@scheduling/graphql';
import {
  buildOptionsFromData,
  FilteredTable,
  MultiSelectTableFilter,
} from 'works/components/filtered-table';
import {
  WorkerDetailQueryCgResponse,
  PaginatedWorksShiftsQueryCgResponse,
  OrderDirection,
  WorksShiftOrder,
} from '@common/graphql/schema';
import { FilterDropdownProps, SorterResult } from 'antd/lib/table/interface';
import { Spacing } from 'works/legacy/lib/style-helper';
import { ShiftTableStatus } from '@scheduling/components';

const { Text } = Typography;

type WorksShiftType = NonNullable<
  NonNullable<
    PaginatedWorksShiftsQueryCgResponse['paginatedWorksShifts']
  >['worksShifts']
>[number];

type Props = {
  worker: WorkerDetailQueryCgResponse['worker'];
};

export function WorkerShiftsTable({ worker }: Props) {
  const LOCALSTORAGE_FILTER_KEY = 'workerShiftsTable';
  const PAGE_SIZE = 10;

  const [paginationAttrs, setPaginationAttrs] = useState({
    limit: PAGE_SIZE,
    offset: 0,
    orderDirection: 'ASC' as OrderDirection,
    orderField: 'START_DATE' as WorksShiftOrder,
  });

  const [filters, setFilters] = useState<{ [key: string]: unknown }>(
    () => ({})
  );

  const [worksShiftsQuery, { loading, error, data: shifts }] = useLazyQuery(
    PaginatedWorksShiftsQuery,
    {
      notifyOnNetworkStatusChange: true,
    }
  );

  useEffect(() => {
    worksShiftsQuery({
      variables: {
        filters: filters,
        workerId: worker.id,
        ...paginationAttrs,
      },
    });
  }, [filters, paginationAttrs, worker.id, worksShiftsQuery]);

  const onFilterChange = (newFilters: { [key: string]: unknown }) => {
    setFilters(newFilters);
  };

  const rolesToFilter: { id: string; name: string }[] = useMemo(() => {
    const fromAllowList = new Set(
      worker.allowLists?.map((allowList) => {
        return {
          id: allowList.role.id,
          name: allowList.role.abbreviation,
        };
      })
    );

    if (worker.role) {
      fromAllowList?.add({
        id: worker.role.id,
        name: worker.role.abbreviation,
      });
    }

    return [...fromAllowList];
  }, [worker.allowLists, worker.role]);

  const dateRangeFilter = (field: string, props: FilterDropdownProps) => {
    if (!props) return;
    return (
      <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
        <DateRangeFilter
          defaultValue={(filters?.[field] as string[])?.map((date: string) =>
            moment(date, 'YYYY-MM-DD')
          )}
          autoFocus={true}
          onSearch={(value) => {
            props.setSelectedKeys(
              value
                ? value.map((date) => moment(date).format?.('YYYY-MM-DD'))
                : []
            );
            props.confirm();
          }}
        />
      </section>
    );
  };

  const columns = [
    {
      title: 'Start Time',
      dataIndex: 'shiftGap',
      key: 'startDate',
      sorter: true,
      filterFormat: 'dateRange',
      filtered: filters.startDate || false,
      filteredValue: filters.startDate || null,
      filterDropdown: (props: FilterDropdownProps) =>
        dateRangeFilter('startDate', props),
      render: (shiftGap: ShiftGapType) => {
        const startTime = moment
          .parseZone(shiftGap.startDatetime)
          .tz(shiftGap.timeZone);
        const date = startTime.format('MM/DD/yyyy');
        const time = startTime.format('HH:mm');

        return (
          <>
            <Text>{date}</Text>
            <br />
            <Text>{time}</Text>
          </>
        );
      },
    },
    {
      title: 'Unit / Cost Center',
      dataIndex: 'shiftGap',
      key: 'unit',
      sorter: true,
      filtered: filters.unit || false,
      filteredValue: filters.unit || null,
      filters: buildOptionsFromData(
        worker.allowedUnits || [],
        (unit: { name: string; costCenter: string; id: string }) => ({
          text: `${unit.name} - ${unit.costCenter}`,
          value: unit.id,
        })
      ),
      filterDropdown: MultiSelectTableFilter,
      render: (gap: ShiftGapType) => (
        <>
          <Text>{gap.organizationUnit.name}</Text>
          <br />
          <Text type="secondary">{gap.organizationUnit.costCenter}</Text>
        </>
      ),
    },
    {
      title: 'Role',
      dataIndex: 'shiftGap',
      key: 'role',
      sorter: true,
      filtered: filters.role || false,
      filteredValue: filters.role || null,
      filters: buildOptionsFromData(rolesToFilter || []),
      render: (gap: ShiftGapType) => gap.role.abbreviation,
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      key: 'duration',
      sorter: true,
      filtered: filters.duration || false,
      filteredValue: filters.duration || null,
      filterFormat: 'hoursRange',
      filterDropdown: ({ setSelectedKeys, confirm }: FilterDropdownProps) => (
        <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
          <NumberRangeFilter
            onSearch={(value) => {
              setSelectedKeys(value || []);
              confirm();
            }}
            min={1}
            max={24}
            defaultValue={filters.duration as [number, number]}
            enforceOrder
          />
        </section>
      ),
      render: (duration: number) => (
        <Text>{duration ? `${duration} hrs` : ''}</Text>
      ),
    },
    {
      title: 'Incentive',
      dataIndex: 'perHourIncentivePay',
      key: 'incentive',
      sorter: true,
      render: (incentivePay: number) => {
        if (incentivePay) {
          return <Currency value={incentivePay / 100} />;
        } else {
          return null;
        }
      },
    },
    {
      title: 'Claimed',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      filtered: filters.createdAt || false,
      filteredValue: filters.createdAt || null,
      filterFormat: 'dateRange',
      filterDropdown: (props: FilterDropdownProps) =>
        dateRangeFilter('createdAt', props),
      render: (createdAt: string, record: WorksShiftType) => {
        const timeZone = record?.shiftGap.timeZone;
        const createdDate = moment
          .parseZone(createdAt)
          .tz(timeZone)
          .format('MM/DD/YYYY, HH:mm');
        return <Text>{createdDate}</Text>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      sorter: true,
      filtered: filters.status || false,
      filteredValue: filters.status || null,
      key: 'status',
      filters: [
        { text: 'Pending Approval', value: 'claimed' },
        { text: 'Rejected', value: 'rejected' },
        { text: 'Cancelled', value: 'cancelled' },
        { text: 'Confirmed', value: 'confirmed' },
        { text: 'Worked', value: 'worked' },
      ],
      render: (status: string) => <ShiftTableStatus status={status} />,
    },
    {
      title: 'Last Updated',
      dataIndex: 'lastStateChangeDatetime',
      key: 'lastUpdatedAt',
      sorter: true,
      filtered: filters.lastUpdatedAt || false,
      filteredValue: filters.lastUpdatedAt || null,
      filterFormat: 'dateRange',
      filterDropdown: (props: FilterDropdownProps) =>
        dateRangeFilter('lastUpdatedAt', props),
      render: (lastStateChangeDatetime: number, record: WorksShiftType) => {
        const timeZone = record.shiftGap.timeZone;
        const lastUpdatedDate = moment
          .parseZone(lastStateChangeDatetime)
          .tz(timeZone)
          .format('MM/DD/YYYY, HH:mm');
        return <Text>{lastUpdatedDate}</Text>;
      },
    },
  ];

  const handleTableChange = (
    pagination: { current: number; pageSize: number },
    newFilters: { [key: string]: unknown },
    sorter: SorterResult<WorksShiftType>
  ) => {
    const pageSize = pagination.pageSize || PAGE_SIZE;
    const newPaginationAttrs = {
      offset: (pagination.current - 1) * pageSize,
      limit: pagination.pageSize || pageSize,
      orderField: 'START_DATE' as WorksShiftOrder,
      orderDirection: 'ASC' as OrderDirection,
    };

    if (sorter?.columnKey) {
      const attributeDict = {
        startDate: 'START_DATE',
        unit: 'UNIT',
        role: 'ROLE',
        duration: 'DURATION',
        incentive: 'INCENTIVE',
        createdAt: 'CREATED_AT',
        status: 'STATUS',
        lastUpdatedAt: 'LAST_UPDATED_AT',
      };

      newPaginationAttrs.orderField = attributeDict[
        sorter.columnKey as keyof typeof attributeDict
      ] as WorksShiftOrder;
    }

    if (sorter?.order) {
      newPaginationAttrs.orderDirection = (
        sorter.order === 'ascend' ? 'ASC' : 'DESC'
      ) as OrderDirection;
    }

    setPaginationAttrs(newPaginationAttrs);
    onFilterChange(newFilters);
  };

  if (error) {
    return <ErrorHandler error={error} log="WorkerShiftsTable" />;
  }

  return (
    <>
      <FilteredTable
        rowKey="id"
        className="worker-shifts-table"
        columns={columns}
        dataSource={shifts?.paginatedWorksShifts?.worksShifts || []}
        loading={loading}
        pagination={{
          ...paginationAttrs,
          total: shifts?.paginatedWorksShifts?.totalCount || null,
        }}
        onChange={handleTableChange}
        onFilterChange={onFilterChange}
        filters={filters}
        localStorageFilterKey={LOCALSTORAGE_FILTER_KEY}
      />
    </>
  );
}
